import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-final-form';

import { snackbar } from '@occc-app/design-system';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferFragment, User_Role } from '@occc/graphql';
import { useOfferUpdateMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import { useUserSelector } from '@occc-app/App/AppContext';
import { mergeDateAndTime, parseDate } from '@occc-app/lib/date';
import { validateScheduleDatesForm } from '@occc-app/routes/CampaignComponents/components/ScheduleDates/schemas';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';

import { FinalizeFormContent } from './FinalizeFormContent';

import { routeCreator } from '../../utils';
import {
  FinalizeFormInitialValues,
  FinalizeFormProps,
  FinalizeFormRenderProps,
  FinalizeFormValues,
} from '../../types';

type FinalizeFormContainer = {
  award: OfferFragment;
  setAllowNavigation?: (allowNavigation: boolean) => void;
  handleCancel: (hasUnsavedChanges?: boolean) => void;
  currentFlowType?: Flow_Types;
};

export const FinalizeFormContainer = ({
  award,
  setAllowNavigation,
  handleCancel,
  currentFlowType = Flow_Types.Update,
}: FinalizeFormContainer) => {
  const navigate = useNavigate();
  const currentUser = useUserSelector();

  const isReadOnlyRole = currentUser?.userRole === User_Role.AdminReadOnly;

  const onCompleted = () => {
    if (setAllowNavigation) {
      setAllowNavigation(true);
    }

    if (currentFlowType === Flow_Types.Configure) {
      navigate(routeCreator.details(award.id));
      snackbar.success('Award has been configured!');
      return;
    }
    snackbar.success('Award was updated.');
  };

  const [offerUpdateMutation, { loading }] = useOfferUpdateMutation({
    onCompleted,
  });

  const initialValues: FinalizeFormInitialValues = useMemo(() => {
    const publishAt = award.publishAt ? parseDate(award.publishAt) : null;
    const expireAt = award.expireAt ? parseDate(award.expireAt) : null;

    return {
      publishAtDate: publishAt || undefined,
      publishAtTime: publishAt || undefined,
      expireAtDate: expireAt || undefined,
      expireAtTime: expireAt || undefined,
    };
  }, [award]);

  const onSubmit = (values: FinalizeFormValues) => {
    const publishAt = mergeDateAndTime(values.publishAtDate, values.publishAtTime);
    const expireAt = mergeDateAndTime(values.expireAtDate, values.expireAtTime);

    offerUpdateMutation({
      variables: {
        input: {
          id: award.id,
          publishAt: publishAt?.toISOString() || null,
          expireAt: expireAt?.toISOString() || null,
        },
      },
    });
  };

  const handleNextButtonClick = useCallback(() => {
    navigate(routeCreator.details(award.id));
  }, [navigate, award.id]);

  const finalizeRenderForm: FinalizeFormProps['render'] = useCallback(
    ({
      handleSubmit,
      values,
      modifiedSinceLastSubmit,
      dirtySinceLastSubmit,
      dirty,
      submitting,
    }: FinalizeFormRenderProps) => {
      const isDirty = modifiedSinceLastSubmit ? dirtySinceLastSubmit : dirty;

      return (
        <OfferForm dirty={!submitting && isDirty} handleSubmit={handleSubmit}>
          <FinalizeFormContent
            values={values}
            isReadOnlyRole={isReadOnlyRole}
            handleCancel={() => handleCancel(isDirty)}
            handleNextButtonClick={handleNextButtonClick}
            currentFlowType={currentFlowType}
            submitting={submitting}
            dirty={isDirty}
            isLoading={loading}
          />
        </OfferForm>
      );
    },
    [isReadOnlyRole, handleNextButtonClick, currentFlowType, handleCancel, loading],
  );

  return (
    <Form<FinalizeFormValues>
      onSubmit={onSubmit}
      render={finalizeRenderForm}
      initialValues={initialValues}
      validate={validateScheduleDatesForm}
    />
  );
};
